import React, { useState } from 'react';
import {
    Box, Button, Typography, IconButton, Modal, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';

const fmtMoney = (v) => v != null ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const ReceiptDetailsCard = ({ receipt }) => {
    const history = useHistory();
    const [imageOpen, setImageOpen] = useState(false);
    const imageUrl = receipt.imageLocation ? `https://cdn.assetsmaven.com/${encodeURIComponent(receipt.imageLocation)}` : null;
    const lineItems = receipt.lineItems || [];

    const handleExport = () => {
        let tsv = "Item\tProduct Code\tPrice\n";
        lineItems.forEach(li => { tsv += `${li.item || ''}\t${li.productCode || ''}\t${li.price || ''}\n`; });
        saveAs(new Blob([tsv], { type: 'text/tsv' }), `${receipt.vendorName || 'receipt'}.tsv`);
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 900, mx: 'auto' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}
                sx={{ mb: 3, color: 'text.secondary', fontWeight: 600, borderRadius: 2, textTransform: 'none',
                    '&:hover': { background: 'rgba(99,102,241,0.06)', color: 'primary.main' } }}>
                Back
            </Button>

            {/* Summary card */}
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', mb: 3 }}>
                {imageUrl && (
                    <Box sx={{ height: 240, overflow: 'hidden', cursor: 'pointer', position: 'relative' }}
                        onClick={() => setImageOpen(true)}>
                        <img src={imageUrl} alt="Receipt" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'contrast(1.1) brightness(1.03)' }} />
                        <Box sx={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff',
                            borderRadius: 1.5, px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                            Click to enlarge
                        </Box>
                    </Box>
                )}
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: '#FBF5EB',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ReceiptLongIcon sx={{ color: '#C89E57', fontSize: '1.3rem' }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {receipt.vendorName || 'Unknown Vendor'}
                                </Typography>
                            </Box>
                            {receipt.invoiceReceiptDate && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>{receipt.invoiceReceiptDate}</Typography>
                            )}
                            {receipt.address && (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{receipt.address}</Typography>
                            )}
                        </Box>
                        {receipt.total != null && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Total</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669' }}>
                                    {fmtMoney(receipt.total)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {/* Quick stats */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        {receipt.subtotal != null && <Chip label={`Subtotal: ${fmtMoney(receipt.subtotal)}`} size="small" variant="outlined" />}
                        {receipt.tax != null && <Chip label={`Tax: ${fmtMoney(receipt.tax)}`} size="small" variant="outlined" />}
                        {lineItems.length > 0 && <Chip label={`${lineItems.length} items`} size="small" variant="outlined" />}
                    </Box>
                </Box>
            </Paper>

            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExport}
                    sx={{ borderRadius: 2, textTransform: 'none' }}>Export CSV</Button>
                {imageUrl && (
                    <Button variant="outlined" size="small" onClick={() => setImageOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none' }}>Download Photo</Button>
                )}
            </Box>

            {/* Items table */}
            {lineItems.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', borderBottom: '2px solid rgba(0,0,0,0.08)' } }}>
                                <TableCell>Item</TableCell>
                                <TableCell>Product Code</TableCell>
                                <TableCell align="right">Price</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lineItems.map((li, i) => (
                                <TableRow key={i} sx={{ '&:nth-of-type(even)': { background: '#F8FAFC' }, '&:hover': { background: '#E8EDF2' } }}>
                                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{li.item || 'Unnamed'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{li.productCode || '—'}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                        {li.price != null ? fmtMoney(li.price) : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {receipt.total != null && (
                                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.1)', fontSize: '0.85rem' } }}>
                                    <TableCell colSpan={2}>TOTAL</TableCell>
                                    <TableCell align="right">{fmtMoney(receipt.total)}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Image lightbox */}
            <Modal open={imageOpen} onClose={() => setImageOpen(false)}>
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setImageOpen(false)}>
                    <IconButton sx={{ position: 'fixed', top: 12, right: 12, color: 'white' }}><CloseIcon /></IconButton>
                    {imageUrl && <img src={imageUrl} alt="Receipt" style={{ maxHeight: '90%', maxWidth: '90%', borderRadius: 8 }} />}
                </Box>
            </Modal>
        </Box>
    );
};

export default ReceiptDetailsCard;
